import { useEffect, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { Alert, Button, Dialog, DialogActions, DialogContent, IconButton, Skeleton } from '@mui/material'
import { publicTicketUrl, ticketService, TicketServiceError, type PublicTicket, type TicketService } from '../../ticketService'
import { TicketCard, ticketDetailsText } from '../TicketCard/TicketCard'
import { drawTicketImage, ticketImageName } from '../../ticketImage'
import styles from './TicketDialog.module.scss'

type Props = Readonly<{
  registrationId: string
  attendeeName: string
  onClose: () => void
  service?: Pick<TicketService, 'issue' | 'resolvePublic'>
  /** Draws the ticket picture; replaceable in tests, which have no canvas. */
  drawImage?: typeof drawTicketImage
}>

type State =
  | Readonly<{ status: 'loading' }>
  | Readonly<{ status: 'ready'; ticket: PublicTicket; url: string }>
  | Readonly<{ status: 'failed'; message: string }>

/**
 * Shows an issued ticket to the collector who registered the attendee, so it
 * can be shown on this phone or its link shared with them. Issuing is idempotent, so
 * asking again returns the existing ticket and its link. The link is a bearer
 * token: it stays in memory here and is never saved on the device.
 */
export function TicketDialog({ registrationId, attendeeName, onClose, service = ticketService, drawImage = drawTicketImage }: Props) {
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState<State>({ status: 'loading' })
  // What the last Copy or Share did, so the buttons can say so.
  const [done, setDone] = useState<'details' | 'link' | null>(null)
  // Text to copy by hand when this device allows neither sharing nor copying.
  const [manual, setManual] = useState<string | null>(null)
  // The ticket as a picture, drawn as soon as the ticket loads: phones only
  // allow sharing straight from a tap, so it must be ready before the tap.
  const [image, setImage] = useState<File | null>(null)

  useEffect(() => {
    let active = true
    void (async () => {
      try {
        const issue = await service.issue(registrationId)
        const ticket = await service.resolvePublic(issue.eventLocator, issue.publicToken)
        if (!active) return
        setState(ticket ? { status: 'ready', ticket, url: publicTicketUrl(issue) } : { status: 'failed', message: 'This ticket is no longer available.' })
      } catch (cause) {
        // Viewing reuses issuing, so its offline message would talk about issuing; say what the person was doing.
        const message = cause instanceof TicketServiceError
          ? cause.code === 'offline' ? 'Reconnect to view this ticket.' : cause.message
          : 'This ticket could not be loaded. Please try again.'
        if (active) setState({ status: 'failed', message })
      }
    })()
    return () => { active = false }
  }, [registrationId, service, attempt])

  useEffect(() => {
    if (state.status !== 'ready') return
    let active = true
    void drawImage(state.ticket, state.url).then(
      (blob) => { if (active) setImage(new File([blob], ticketImageName(state.ticket), { type: 'image/png' })) },
      () => { if (active) setImage(null) }, // No picture: Share ticket sends the link instead.
    )
    return () => { active = false }
  }, [state, drawImage])

  const copyText = async (text: string, what: 'details' | 'link') => {
    setManual(null)
    try {
      await navigator.clipboard.writeText(text)
      setDone(what)
    } catch {
      setDone(null)
      setManual(text)
    }
  }

  /** The event, date, time, venue and ticket link as text, ready to paste into any message. */
  const copyDetails = async () => {
    if (state.status !== 'ready') return
    await copyText(ticketDetailsText(state.ticket, state.url), 'details')
  }

  /**
   * The phone's share sheet (WhatsApp, Messages, email) with the ticket as a
   * picture and the details and link as the message. Once the picture is in
   * their chat or photos, the QR code works at the door with no signal.
   * Where pictures can't be shared, the link is shared instead, and where
   * there's no share sheet at all (some desktop browsers) it is copied.
   */
  const share = async () => {
    if (state.status !== 'ready') return
    const title = `Ticket for ${state.ticket.eventName}`
    const details = ticketDetailsText(state.ticket, state.url)
    const attempts: ShareData[] = [
      ...(image ? [{ title, text: details, files: [image] }] : []),
      // The share sheet adds the link itself, so this message leaves it out.
      { title, text: details.replace(`: ${state.url}`, ''), url: state.url },
    ]
    if (typeof navigator.share === 'function') {
      for (const data of attempts) {
        if (navigator.canShare && !navigator.canShare(data)) continue
        try {
          await navigator.share(data)
          return
        } catch (error) {
          // Closing the share sheet is a choice, not a failure.
          if (error instanceof DOMException && error.name === 'AbortError') return
        }
      }
    }
    await copyText(state.url, 'link')
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" slotProps={{ paper: { 'aria-label': `Ticket for ${attendeeName}`, className: styles.paper } }}>
      {/* Close sits top right, where people look for it. */}
      <div className={styles.header}>
        <span className={styles.heading}>Ticket for {attendeeName}</span>
        <IconButton aria-label="Close" onClick={onClose} className={styles.close} edge="end" size="small"><CloseIcon /></IconButton>
      </div>
      <DialogContent className={styles.content}>
        {state.status === 'loading' ? <Skeleton variant="rounded" className={styles.loading} aria-label="Loading ticket" /> : null}
        {state.status === 'failed' ? (
          <Alert severity="error" action={<Button size="small" onClick={() => { setState({ status: 'loading' }); setAttempt((value) => value + 1) }}>Try again</Button>}>{state.message}</Alert>
        ) : null}
        {manual ? (
          <Alert severity="warning" className={styles.notice}>
            Copying isn’t available on this device. Select this and copy it yourself:
            <span className={styles.link}>{manual}</span>
          </Alert>
        ) : null}
        {state.status === 'ready' ? <TicketCard ticket={state.ticket} ticketUrl={state.url} heading="h2" fitScreen /> : null}
      </DialogContent>
      {/* One row, so the ticket and its QR code fit on the screen without scrolling. */}
      <DialogActions className={styles.actions} disableSpacing>
        {state.status === 'ready' ? (
          <>
            <Button variant="outlined" onClick={() => void copyDetails()}>{done === 'details' ? 'Copied' : 'Copy details'}</Button>
            <Button variant="contained" onClick={() => void share()}>{done === 'link' ? 'Link copied' : 'Share ticket'}</Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}
